import React, { useState } from 'react';
import './BilgiFormuSection.css';
import { formatPhoneNumber } from '../../utils/phoneFormatter';
import { sendSiparisEmail } from '../../utils/emailService';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { 
  validateAdSoyad, 
  validateTelefon, 
  validateEmailField, 
  validateMesaj 
} from '../../utils/formValidation';

const BilgiFormuSection: React.FC = () => {
  const [formData, setFormData] = useState({
    ad_soyad: '',
    telefon: '',
    email: '',
    mesaj: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast, toastMessage, show } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    let newValue = value;
    if (name === 'telefon') {
      newValue = formatPhoneNumber(value);
    }
    
    setFormData({
      ...formData,
      [name]: newValue,
    });
    
    if (touched[name]) {
      validateField(name, newValue);
    }
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setTouched({
      ...touched,
      [name]: true,
    });
    
    validateField(name, value);
  };

  const validateField = (name: string, value: string) => {
    let validation: { isValid: boolean; error: string } = { isValid: true, error: '' };
    
    switch (name) {
      case 'ad_soyad':
        validation = validateAdSoyad(value);
        break;
      case 'telefon':
        validation = validateTelefon(value, true);
        break;
      case 'email':
        validation = validateEmailField(value, true);
        break;
      case 'mesaj':
        validation = validateMesaj(value, false);
        break;
      default:
        break;
    }
    
    if (!validation.isValid) {
      setErrors({
        ...errors,
        [name]: validation.error,
      });
    } else {
      const newErrors = { ...errors };
      delete newErrors[name];
      setErrors(newErrors);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    const newTouched: { [key: string]: boolean } = {};

    Object.keys(formData).forEach(key => {
      newTouched[key] = true;
    });
    setTouched(newTouched);

    const adSoyadValidation = validateAdSoyad(formData.ad_soyad);
    if (!adSoyadValidation.isValid) {
      newErrors.ad_soyad = adSoyadValidation.error;
    }

    const telefonValidation = validateTelefon(formData.telefon, true);
    if (!telefonValidation.isValid) {
      newErrors.telefon = telefonValidation.error;
    }

    const emailValidation = validateEmailField(formData.email, true);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await sendSiparisEmail(formData);
      show('Form başarıyla gönderildi!');
      
      setFormData({
        ad_soyad: '',
        telefon: '',
        email: '',
        mesaj: '',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Form gönderilirken bir hata oluştu. Lütfen bilgilerinizi kontrol edip tekrar deneyin.';
      show(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="bilgi-formu" className="bilgi-formu-section">
      <div className="bilgi-formu-container">
        <div className="bilgi-formu-content">
          <div className="bilgi-formu-form-container">
            <h2 className="bilgi-formu-title">
              <span className="highlight">THERMOMİX TM7</span><br />
              SİPARİŞ FORMU
            </h2>
            <form id="bilgi-form" onSubmit={handleSubmit} className="bilgi-form">
              <div className="form-group">
                <input
                  type="text"
                  name="ad_soyad"
                  placeholder="Adınız Soyadınız"
                  value={formData.ad_soyad}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`form-input ${errors.ad_soyad ? 'error' : ''}`}
                  aria-label="Adınız Soyadınız"
                  aria-required="true"
                  aria-invalid={errors.ad_soyad ? 'true' : 'false'}
                  aria-describedby={errors.ad_soyad ? 'ad_soyad-error' : undefined}
                  disabled={isSubmitting}
                />
                {errors.ad_soyad && <span id="ad_soyad-error" className="error-message" role="alert">{errors.ad_soyad}</span>}
              </div>
              <div className="form-group">
                <input
                  type="tel"
                  name="telefon"
                  placeholder="Telefon Numaranız (+90 XXX XXX XX XX)"
                  value={formData.telefon}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`form-input ${errors.telefon ? 'error' : ''}`}
                  aria-label="Telefon Numaranız"
                  aria-required="true"
                  aria-invalid={errors.telefon ? 'true' : 'false'}
                  aria-describedby={errors.telefon ? 'telefon-error' : undefined}
                  disabled={isSubmitting}
                />
                {errors.telefon && <span id="telefon-error" className="error-message" role="alert">{errors.telefon}</span>}
              </div>
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  placeholder="E-Posta Adresiniz"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  aria-label="E-Posta Adresiniz"
                  aria-required="true"
                  aria-invalid={errors.email ? 'true' : 'false'}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  disabled={isSubmitting}
                />
                {errors.email && <span id="email-error" className="error-message" role="alert">{errors.email}</span>}
              </div>
              <div className="form-group">
                <textarea
                  name="mesaj"
                  rows={2}
                  placeholder="Adresiniz"
                  value={formData.mesaj}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="form-textarea"
                  aria-label="Adresiniz"
                  disabled={isSubmitting}
                ></textarea>
              </div>
              <button 
                type="submit" 
                className="form-submit-btn"
                disabled={isSubmitting}
                aria-label={isSubmitting ? 'Form gönderiliyor' : 'Sipariş formunu gönder'}
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="small" color="white" />
                    <span style={{ marginLeft: '0.5rem' }}>Gönderiliyor...</span>
                  </>
                ) : (
                  'Sipariş Formunu Gönder'
                )}
              </button>
            </form>
          </div>
          <div className="bilgi-formu-image">
            <img 
              src="/assets/img/tm7/akilliyemekrobotu-thermomix-tm7-bilgi-formu.webp"
              alt="Thermomix TM7 Bilgi Formu"
              className="form-image"
              loading="lazy"
            />
          </div>
        </div>
      </div>
      {showToast && (
        <div className="toast-notification">
          {toastMessage}
        </div>
      )}
    </section>
  );
};

export default BilgiFormuSection;

